import React, { useEffect } from 'react';
import { UseSensorTypesReturn } from './hooks/useSensorTypes';
import { isDisplacementCategory } from 'features/calibration/utils/displacementModelFamily';


interface SensorFilterFormProps {
  individualCalibrationData: any;
  setIndividualCalibrationData: React.Dispatch<React.SetStateAction<any>>;
  sensorTypes: UseSensorTypesReturn;
  onSensorTypeChange?: (sensorType: string) => void;
  onCategoryChange?: (category: string) => void;
  onModelChange?: (model: string) => void;
  onRangeChange?: (range: string) => void;
}

export const SensorFilterForm: React.FC<SensorFilterFormProps> = ({
  individualCalibrationData,
  setIndividualCalibrationData,
  sensorTypes,
  onSensorTypeChange,
  onCategoryChange,
  onModelChange,
  onRangeChange,
}) => {
  const isDisplacement = isDisplacementCategory(individualCalibrationData.category);
  const showModelFamilyDropdown = isDisplacement && sensorTypes.familySelectionRequired;
  const resolvedModelFamily =
    isDisplacement && !sensorTypes.familySelectionRequired
      ? sensorTypes.availableModelFamilies[0]
      : null;

  useEffect(() => {
    if (
      resolvedModelFamily &&
      individualCalibrationData.modelFamily !== resolvedModelFamily.value
    ) {
      setIndividualCalibrationData((prev: any) => ({
        ...prev,
        modelFamily: resolvedModelFamily.value,
        model: '',
        range: '',
      }));
      sensorTypes.updateModels(
        individualCalibrationData.sensorType,
        individualCalibrationData.category,
        resolvedModelFamily.value
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedModelFamily?.value]);

  const handleSensorTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const sensorType = e.target.value;
    setIndividualCalibrationData((prev: any) => ({
      ...prev,
      sensorType,
      category: '',
      modelFamily: '',
      model: '',
      range: '',
    }));
    sensorTypes.updateCategories(sensorType);
    onSensorTypeChange?.(sensorType);
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const category = e.target.value;
    setIndividualCalibrationData((prev: any) => ({
      ...prev,
      category,
      modelFamily: '',
      model: '',
      range: '',
    }));
    if (isDisplacementCategory(category)) {
      sensorTypes.updateModelFamilies(individualCalibrationData.sensorType, category);
    } else {
      sensorTypes.updateModels(individualCalibrationData.sensorType, category);
    }
    onCategoryChange?.(category);
  };

  const handleModelFamilyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const modelFamily = e.target.value;
    setIndividualCalibrationData((prev: any) => ({
      ...prev,
      modelFamily,
      model: '',
      range: '',
    }));
    sensorTypes.updateModels(
      individualCalibrationData.sensorType,
      individualCalibrationData.category,
      modelFamily
    );
  };

  const handleModelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const model = e.target.value;
    setIndividualCalibrationData((prev: any) => ({
      ...prev,
      model,
      range: '',
    }));
    sensorTypes.updateRanges(
      individualCalibrationData.sensorType,
      individualCalibrationData.category,
      model
    );
    onModelChange?.(model);
  };

  const handleRangeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const range = e.target.value;
    setIndividualCalibrationData((prev: any) => ({
      ...prev,
      range,
    }));
    onRangeChange?.(range);
  };

  const range = individualCalibrationData.range ?? '';

  return (
    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
      <h3 className="text-lg font-medium text-gray-900 mb-4">
        Sensor Filters
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Sensor Type <span className="text-red-500 ml-1">*</span>
          </label>
          <select
            value={individualCalibrationData.sensorType ?? ''}
            onChange={handleSensorTypeChange}
            className="input w-full"
            disabled={sensorTypes.isLoadingSensorTypes}
          >
            <option value="">
              {sensorTypes.isLoadingSensorTypes
                ? 'Loading...'
                : 'Select sensor type'}
            </option>
            {sensorTypes.availableSensorTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Category <span className="text-red-500 ml-1">*</span>
          </label>
          <select
            value={individualCalibrationData.category ?? ''}
            onChange={handleCategoryChange}
            className="input w-full"
            disabled={!individualCalibrationData.sensorType}
          >
            <option value="">Select category</option>
            {sensorTypes.availableCategories.map((category) => (
              <option key={category.value} value={category.value}>
                {category.label}
              </option>
            ))}
          </select>
        </div>

        {showModelFamilyDropdown && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Displacement Sensor Type <span className="text-red-500 ml-1">*</span>
            </label>
            <select
              value={individualCalibrationData.modelFamily ?? ''}
              onChange={handleModelFamilyChange}
              className="input w-full"
              disabled={
                !individualCalibrationData.sensorType ||
                !individualCalibrationData.category
              }
            >
              <option value="">Select sensor type (e.g. EDE-VXX-RC)</option>
              {sensorTypes.availableModelFamilies.map((family) => (
                <option key={family.value} value={family.value}>
                  {family.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {isDisplacement && resolvedModelFamily && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Displacement Sensor Type
            </label>
            <div className="input w-full bg-gray-100 flex items-center">
              {resolvedModelFamily.label}
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Model <span className="text-red-500 ml-1">*</span>
          </label>
          <select
            value={individualCalibrationData.model ?? ''}
            onChange={handleModelChange}
            className="input w-full"
            disabled={
              !individualCalibrationData.sensorType ||
              !individualCalibrationData.category ||
              (isDisplacement && !individualCalibrationData.modelFamily)
            }
          >
            <option value="">Select model</option>
            {sensorTypes.availableModels.map((model) => (
              <option key={model.value} value={model.value}>
                {model.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Range <span className="text-red-500 ml-1">*</span>
          </label>
          <select
            value={range}
            onChange={handleRangeChange}
            className="input w-full"
            disabled={
              !individualCalibrationData.sensorType ||
              !individualCalibrationData.category ||
              !individualCalibrationData.model
            }
          >
            <option value="">Select range</option>
            {sensorTypes.availableRanges.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};
